import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Typography, 
  Box, 
  Container,
  Button
} from '@mui/material';
import { ArrowLeft } from 'lucide-react';
import Module1Service from '../services/module1Service';
import blobService from '../services/storage/blobService';
import ClientWorkflowTable from '../components/ClientWorkflowTable';
import { useToast } from '../context/ToastContext';
import { useAlwaysDatabaseMode } from '../context/DatabaseModeContext';
import workflowService from '../services/api/workflowService';
import { Header } from '@/components/shared/Header';
import { useWorkflowNavigation } from '../hooks/useWorkflowNavigation';

// Define the DatabaseClient interface
interface DatabaseClient {
  client_id: number;
  name: string;
  client_code?: string;
  active?: boolean;
  process_status?: 'new' | 'in_progress' | 'completed';
  user_count?: number;
}

const StartProcess = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { isUsingDatabaseApproach } = useAlwaysDatabaseMode();
  // Add ref to track initial load
  const initialLoadCompleted = useRef(false);
  
  // Rest of the state variables
  const [clients, setClients] = useState<string[]>([]);
  const [dbClients, setDbClients] = useState<DatabaseClient[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [workflows, setWorkflows] = useState<Record<string, any[]>>({});
  const [loadingWorkflows, setLoadingWorkflows] = useState(false);
  const [expandedClient, setExpandedClient] = useState<string | null>(null);
  const [expandedWorkflow, setExpandedWorkflow] = useState<{clientId: string, workflowId: string} | null>(null);
  const [files, setFiles] = useState<Record<string, any[]>>({});
  const [loadingFiles, setLoadingFiles] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalClients, setTotalClients] = useState(0);
  const [searchTerm, setSearchTerm] = useState(() => {
    // Load from sessionStorage or default to empty string
    return sessionStorage.getItem('clientSearchTerm') || '';
  });
  const [processStatus, setProcessStatus] = useState(() => {
    // Load from sessionStorage or default to 'all'
    return sessionStorage.getItem('clientProcessStatus') || 'all';
  });
  const [sortBy, setSortBy] = useState(() => {
    return sessionStorage.getItem('clientSortBy') || 'client_id';
  });
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(() => {
    return (sessionStorage.getItem('clientSortOrder') as 'asc' | 'desc') || 'desc';
  });
  
  const [dbClientId, setDbClientId] = useState<number | null>(() => {
    // Try to load from localStorage
    const storedId = localStorage.getItem('db_client_id');
    return storedId ? parseInt(storedId, 10) : null;
  });

  // Load client list with pagination, search, and process status filter
  const loadClients = async (
    page: number = currentPage, 
    size: number = pageSize, 
    search: string = searchTerm, 
    status: string = processStatus,
    sort: string = sortBy,
    order: 'asc' | 'desc' = sortOrder
  ) => {
    // Start a timer to show loading state only if the request takes more than 300ms
    const loadingTimer = setTimeout(() => {
      setLoadingClients(true);
    }, 300);
    
    try {
      if (isUsingDatabaseApproach) {
        // Use database-first approach with pagination, search, status filter, and sorting
        const offset = (page - 1) * size;
        const result = await workflowService.getDbClientsPaginated({
          limit: size,
          offset: offset,
          active_only: true,
          search: search || undefined, // Only pass search if it's not empty
          process_status: status || undefined, // Only pass process_status if it's not empty
          sort_by: sort,
          sort_order: order
        });
        
        setDbClients(result.clients);
        setTotalClients(result.total);
        
        // If we haven't set a dbClientId yet but have clients, set the first one
        if (!dbClientId && result.clients.length > 0) {
          const firstClientId = result.clients[0].client_id;
          setDbClientId(firstClientId);
          localStorage.setItem('db_client_id', firstClientId.toString());
        }
      } else {
        // Use blob storage approach
        const clientList = await Module1Service.getInstance().getClientIds();
        setClients(clientList.filter(id => id !== 'default'));
      }
    } catch (error) {
      console.error('Error loading clients:', error);
      showToast(`Failed to load clients: ${isUsingDatabaseApproach ? 'Database' : 'Blob storage'} error`, 'error');
    } finally {
      clearTimeout(loadingTimer);
      setLoadingClients(false);
    }
  };
  
  // Handle page change
  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    loadClients(page);
  };
  
  // Handle page size change
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page
    // Reload with new page size - pass it explicitly since state update is async
    loadClients(1, newPageSize);
  };
  
  // Handle search change - memoized to prevent infinite loops
  const handleSearchChange = useCallback((newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);
    setCurrentPage(1); // Reset to first page when searching
    // Reload with search term
    loadClients(1, pageSize, newSearchTerm, processStatus, sortBy, sortOrder);
  }, [pageSize, processStatus, sortBy, sortOrder]);
  
  // Handle process status filter change
  const handleProcessStatusChange = (newStatus: string) => {
    setProcessStatus(newStatus);
    setCurrentPage(1); // Reset to first page when changing filter
    // Reload with new status filter
    loadClients(1, pageSize, searchTerm, newStatus);
  };

  // Handle sort by change
  const handleSortByChange = (newSortBy: string) => {
    setSortBy(newSortBy);
    setCurrentPage(1); // Reset to first page when changing sort
    loadClients(1, pageSize, searchTerm, processStatus, newSortBy, sortOrder);
  };

  // Handle sort order toggle
  const handleSortOrderToggle = () => {
    const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    setSortOrder(newOrder);
    loadClients(currentPage, pageSize, searchTerm, processStatus, sortBy, newOrder);
  };
  
  // Save search term to sessionStorage when it changes
  useEffect(() => {
    sessionStorage.setItem('clientSearchTerm', searchTerm);
  }, [searchTerm]);

  // Save process status filter to sessionStorage when it changes
  useEffect(() => {
    sessionStorage.setItem('clientProcessStatus', processStatus);
  }, [processStatus]);

  // Save sorting preferences to sessionStorage
  useEffect(() => {
    sessionStorage.setItem('clientSortBy', sortBy);
  }, [sortBy]);

  useEffect(() => {
    sessionStorage.setItem('clientSortOrder', sortOrder);
  }, [sortOrder]);

  // Load clients on component mount - only once
  useEffect(() => {
    if (!initialLoadCompleted.current) {
      console.log('Loading clients...');
      loadClients();
      initialLoadCompleted.current = true;
    }
  }, []);

  // Toggle client expansion to load workflows
  const toggleClientExpansion = async (clientId: string | number) => {
    const clientKey = clientId.toString();
    
    if (expandedClient === clientKey) {
      setExpandedClient(null);
      return;
    }
    
    setExpandedClient(clientKey);
    
    if (!workflows[clientKey]) {
      // Start a timer to show loading state only if the request takes more than 200ms
      const loadingTimer = setTimeout(() => {
        setLoadingWorkflows(true);
      }, 200);
      
      try {
        if (isUsingDatabaseApproach && typeof clientId === 'number') {
          // Use database-first approach
          const response = await workflowService.getDbClientWorkflows(clientId);
          setWorkflows(prev => ({
            ...prev,
            [clientKey]: response.workflows || []
          }));
        } else {
          // Use blob storage approach
          const response = await Module1Service.getInstance().getWorkflowsByClientId(clientKey);
          setWorkflows(prev => ({
            ...prev,
            [clientKey]: response || []
          }));
        }
      } catch (error) {
        console.error(`Error loading workflows for client ${clientId}:`, error);
        showToast(`Failed to load workflows for ${clientId}`, 'error');
      } finally {
        clearTimeout(loadingTimer);
        setLoadingWorkflows(false);
      }
    }
  };

  // Toggle workflow expansion to load files
  const toggleWorkflowExpansion = async (clientId: string | number, workflowId: string | number) => {
    const clientIdStr = clientId.toString();
    const workflowIdStr = workflowId.toString();
    const key = `${clientIdStr}:${workflowIdStr}`;
    
    if (expandedWorkflow && expandedWorkflow.clientId === clientIdStr && expandedWorkflow.workflowId === workflowIdStr) {
      setExpandedWorkflow(null);
      return;
    }
    
    setExpandedWorkflow({ clientId: clientIdStr, workflowId: workflowIdStr });
    
    if (!files[key]) {
      try {
        setLoadingFiles(true);
        const fileList = await blobService.listFiles({
          containerName: 'client-data',
          clientId: clientIdStr,
          workflowId: workflowIdStr
        });
        
        setFiles(prev => ({
          ...prev,
          [key]: fileList || []
        }));
      } catch (error) {
        console.error(`Error loading files for workflow ${workflowId}:`, error);
        showToast(`Failed to load files for workflow`, 'error');
      } finally {
        setLoadingFiles(false);
      }
    }
  };

  // Navigate to workflow details using custom hook
  const { openWorkflow } = useWorkflowNavigation();
  
  const goToWorkflow = (clientId: string | number, workflowId: string | number, workflowName?: string) => {
    // workflowName parameter kept for API compatibility but not used - hook fetches workflow data
    openWorkflow({ clientId, workflowId });
  };

  // View or download file
  const handleFileAction = async (file: any) => {
    try {
      // Explicitly pass 'client-data' as the container name to ensure correct URL generation
      const downloadUrl = await blobService.getDownloadUrl(file.path, 'client-data');
      window.open(downloadUrl, '_blank');
    } catch (error) {
      console.error('Error opening file:', error);
      showToast('Failed to open file', 'error');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Header
          title="Start New Process"
          subtitle="Select a client and workflow to start the process." 
          onClick={() => navigate('/dashboard')}
          btnText="Back to Dashboard"
          startIcon={<ArrowLeft size={20} />}

        />
      
      <ClientWorkflowTable
        clients={clients}
        dbClients={dbClients}
        loadingClients={loadingClients}
        isUsingDatabaseApproach={isUsingDatabaseApproach}
        workflows={workflows}
        loadingWorkflows={loadingWorkflows}
        expandedClient={expandedClient}
        expandedWorkflow={expandedWorkflow}
        files={files}
        loadingFiles={loadingFiles}
        toggleClientExpansion={toggleClientExpansion}
        toggleWorkflowExpansion={toggleWorkflowExpansion}
        goToWorkflow={goToWorkflow}
        handleFileAction={handleFileAction}
        setDbClientId={setDbClientId}
        loadClients={loadClients}
        currentPage={currentPage}
        pageSize={pageSize}
        totalClients={totalClients}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        processStatus={processStatus}
        onProcessStatusChange={handleProcessStatusChange}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortByChange={handleSortByChange}
        onSortOrderToggle={handleSortOrderToggle}
      />
    </Container>
  );
};

export default StartProcess;
